import { useEffect, useRef, useState } from 'react'
import { PHASE_LABEL, PHASE_TONE, phaseProgress, formatMoney } from '../../lib/journey.js'
import { useAccess } from '../../lib/access.jsx'
import { ACTIONS, sameAccount, normalizeResource } from '../../lib/rbac.js'
import styles from './AccountBoard.module.css'

/**
 * How many cards exist in the DOM before scrolling grows the list.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THIS BOARD USED TO RENDER EVERY ACCOUNT AT ONCE.
 *
 * Measured on production 2026-08-27: 3,101 cards, 49,128 DOM nodes, a page
 * 320,775px tall. Clicking "All accounts" froze the renderer for over thirty
 * seconds on a desktop — the screenshot tool timed out waiting for a paint.
 * On a phone that is not slow, it is dead, which is what "the customer
 * journey is down" turned out to mean.
 *
 * The DOM was only half of it. Every card calls `can()` for account control,
 * so the old render also ran 3,101 permission evaluations before it could
 * paint anything at all.
 *
 * 48 fills more than a screen at any width, so the first paint is immediate
 * and scrolling never reaches the edge of what is rendered.
 * ─────────────────────────────────────────────────────────────────────────
 */
const PAGE = 48

/** Account (deal) cards for the current filter. */
export default function AccountBoard({ items, emptyHint, onOpen }) {
  const [limit, setLimit] = useState(PAGE)
  const sentinel = useRef(null)

  /* A new filter is a new list, so the window starts over. Without this,
   * scrolling deep into "All accounts" and then narrowing to one rep would
   * keep the grown limit and re-render far more than the filter asked for. */
  useEffect(() => { setLimit(PAGE) }, [items])

  useEffect(() => {
    const el = sentinel.current
    if (!el || limit >= items.length) return
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setLimit((n) => n + PAGE)
    }, { rootMargin: '600px' }) // grow before the user reaches the bottom
    io.observe(el)
    return () => io.disconnect()
  }, [limit, items.length])

  if (!items.length) {
    return (
      <div className={styles.empty}>
        <div className={styles.emptyTitle}>No accounts in this view</div>
        <p>{emptyHint || 'Try a different phase or clear the search.'}</p>
      </div>
    )
  }

  const shown = items.slice(0, limit)
  const more = items.length - shown.length

  return (
    <>
      <div className={styles.board}>
        {shown.map((d) => (
          <AccountCard key={d.id} d={d} onOpen={onOpen} />
        ))}
      </div>

      {more > 0 && (
        <div ref={sentinel} className={styles.more}>
          {/* Named, not a bare spinner. "3,053 more" tells you the filter is
            * too wide; an endless scroll tells you nothing. */}
          <span>Showing {shown.length.toLocaleString()} of {items.length.toLocaleString()} · {more.toLocaleString()} more</span>
          <button type="button" className={styles.moreBtn} onClick={() => setLimit((n) => n + PAGE * 4)}>
            Load more
          </button>
        </div>
      )}
    </>
  )
}

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '—'
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase()
}

function AccountCard({ d, onOpen }) {
  const tone = PHASE_TONE[d.phase] || 'muted'
  const progress = phaseProgress(d.phase)
  // Prefer the real derived detail; otherwise fall back to the feed's own raw
  // status rather than dressing up a phase we only half know.
  const stageLine =
    d.phase === 'approvals' && d.currentGroup ? `Awaiting: ${d.currentGroup.title}`
      : d.phase === 'production' && d.productionStage ? `Stage ${d.productionStage.n}/14 · ${d.productionStage.title}`
      : d.phase === 'production' ? `${d.dealstageRaw || 'Production'} · manufacturing milestone not recorded`
      : d.dealstageRaw && d.source === 'clickup' ? d.dealstageRaw
      : PHASE_LABEL[d.phase] || d.dealstageRaw || '—'

  return (
    <article className={styles.cardWrap}>
    <button type="button" className={styles.card} data-tone={tone} onClick={() => onOpen(d)}>
      <div className={styles.top}>
        <div className={styles.identity}>
          <div className={styles.account}>{d.org}</div>
          <div className={styles.order}>{d.orderNo || d.dealNo} · {d.sport}{d.qty ? ` · ${d.qty} units` : ''}</div>
        </div>
        <span className={styles.phaseTag} data-tone={tone}>{PHASE_LABEL[d.phase]}</span>
      </div>

      {progress != null && (
        <div className={styles.progress} aria-hidden="true">
          <span className={styles.progressFill} style={{ width: `${progress * 100}%` }} />
        </div>
      )}

      <div className={styles.stageLine}>{d.atRisk && <span className={styles.risk}>At risk · </span>}{stageLine}</div>

      <div className={styles.meta}>
        {d.owner && d.owner !== 'Unassigned' ? (
          <span className={styles.owner}><span className={styles.avatar}>{initials(d.owner)}</span>{d.owner}</span>
        ) : (
          <span className={styles.ownerNone}>Unassigned</span>
        )}
        {d.amount != null && <span className={styles.value}>{formatMoney(d.amount)}</span>}
      </div>
    </button>

      <AccountControl d={d} />
    </article>
  )
}

/**
 * PROOF OF PATTERN — the first module consumer of the shared permission layer.
 *
 * Nothing here re-implements a rule: the button asks `can()` about THIS account
 * and renders exactly what it is told. An exec sees it on every card, a rep on
 * their own book only, and a session with no role sees an honest explanation of
 * why the control is unavailable rather than a button that would fail.
 */
function AccountControl({ d }) {
  const { can, why, actingAs, startActingAs, stopActingAs } = useAccess()
  const allowed = can(ACTIONS.ACTING_START, d)
  const active = actingAs && sameAccount(actingAs, normalizeResource(d))

  if (d.journeyScope?.entity === 'orders') {
    return <div className={styles.control}><span className={styles.controlDenied}>Order read scope only; no CRM account control is established.</span></div>
  }

  if (active) {
    return (
      <div className={styles.control}>
        <span className={styles.controlActive}>You are acting as this account</span>
        <button type="button" className={styles.controlBtn} onClick={stopActingAs}>
          Exit
        </button>
      </div>
    )
  }

  if (!allowed) {
    return (
      <div className={styles.control}>
        <span className={styles.controlDenied} title={why(ACTIONS.ACTING_START, d)}>
          {why(ACTIONS.ACTING_START, d)}
        </span>
      </div>
    )
  }

  return (
    <div className={styles.control}>
      <span className={styles.controlHint}>Account control</span>
      <button
        type="button"
        className={styles.controlBtn}
        onClick={() => startActingAs(d)}
      >
        Take control
      </button>
    </div>
  )
}

